import { ReactNode } from "react";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { Crown, Lock, Sparkles, Bell } from "lucide-react";
import { Link } from "wouter";

// ─── Coming Soon ──────────────────────────────────────────────────────────────

interface ComingSoonProps {
  feature: string;
  description?: string;
  icon?: ReactNode;
  eta?: string;
}

export function ComingSoon({ feature, description, icon, eta }: ComingSoonProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center border-2 border-dashed border-border rounded-2xl bg-gradient-to-b from-muted/40 to-muted/10">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
        {icon ?? <Sparkles className="w-8 h-8 text-primary" />}
      </div>
      <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mb-3">
        <Sparkles className="w-3 h-3" />
        Coming Soon
      </div>
      <h3 className="text-xl font-bold mb-2">{feature}</h3>
      <p className="text-muted-foreground max-w-md text-sm leading-relaxed">
        {description || `${feature} is currently being built and will be available soon.`}
      </p>
      {eta && (
        <p className="text-xs text-muted-foreground mt-3 bg-muted px-3 py-1.5 rounded-full">
          Expected: {eta}
        </p>
      )}
      <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 rounded-xl px-4 py-3 max-w-sm">
        <Bell className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
        <span>We'll notify you by email as soon as this feature launches.</span>
      </div>
    </div>
  );
}

// ─── Enterprise Gate ───────────────────────────────────────────────────────────

interface EnterpriseGateProps {
  children?: ReactNode;
  feature: string;
  description?: string;
  comingSoon?: boolean;
}

export function EnterpriseGate({ children, feature, description, comingSoon }: EnterpriseGateProps) {
  const { store } = useAuth();

  if (store?.plan === "enterprise") {
    if (comingSoon) {
      return (
        <ComingSoon
          feature={feature}
          description={description || `${feature} is currently being built and will be available soon — even for Enterprise users.`}
        />
      );
    }
    return <>{children}</>;
  }

  if (comingSoon) {
    return (
      <ComingSoon
        feature={feature}
        description={description}
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center border-2 border-dashed border-border rounded-2xl bg-muted/30">
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
        <Crown className="w-8 h-8 text-amber-600" />
      </div>
      <h3 className="text-xl font-bold mb-2">{feature}</h3>
      <p className="text-muted-foreground mb-1 max-w-md">
        {description || `${feature} is an Enterprise-only feature.`}
      </p>
      <p className="text-sm text-muted-foreground mb-6">
        Your current plan: <span className="font-medium capitalize">{store?.plan || "trial"}</span>
      </p>
      <Link href="/subscribe">
        <Button className="gap-2">
          <Crown className="w-4 h-4" />
          Upgrade to Enterprise
        </Button>
      </Link>
    </div>
  );
}

// ─── Plan Gate ────────────────────────────────────────────────────────────────

interface PlanGateProps {
  children: ReactNode;
  allowedPlans: string[];
  feature: string;
}

export function PlanGate({ children, allowedPlans, feature }: PlanGateProps) {
  const { store } = useAuth();
  if (allowedPlans.includes(store?.plan || "trial")) return <>{children}</>;

  return (
    <div className="flex flex-col items-center justify-center py-10 px-8 text-center border-2 border-dashed border-border rounded-xl bg-muted/20">
      <Lock className="w-10 h-10 text-muted-foreground mb-3" />
      <h3 className="text-lg font-bold mb-2">{feature}</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Available on higher plans. Current plan: <span className="capitalize font-medium">{store?.plan || "trial"}</span>
      </p>
      <Link href="/subscribe">
        <Button variant="outline" size="sm">View Plans</Button>
      </Link>
    </div>
  );
}
